import { ColumnComponent, ExcelExportComponent, ExcelExportData } from "@progress/kendo-angular-excel-export";
import { HtlService } from "../htl/htl.service";
import { HelicoptersService } from "../helicopters/helicopters.service";
import { LocalStorageService, UserLocalStorageObject } from "../shared/local-storage.service";
import { BootstrapService } from "../shared/bootstrap/bootstrap.service";
import { FlightsService } from "../flights-calendar/flights.service";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Helicopter } from "../shared/models/helicopter.model";
import { MatDialog } from "@angular/material/dialog";
import { ExportDataDialogueComponent } from "./export-data-dialogue/export-data-dialogue.component";
import { AppConfigType } from "../shared/models/appConfig.model";
import { allExportFlightColumns, columnTitlesMap, DisplayedColumn } from "./export-data.constants";
import { DataStateChangeEvent, ExcelExportEvent, GridComponent, GridDataResult } from "@progress/kendo-angular-grid";
import { process, SortDescriptor, State, CompositeFilterDescriptor, FilterDescriptor, distinct, filterBy } from "@progress/kendo-data-query";
import { Component, ElementRef, OnInit, ViewChild, inject } from "@angular/core";
import { AccessValuePipe } from "../shared/pipes/access-value.pipe";
import { ExportDataColumnsDialogueComponent } from "./export-data-columns-dialogue/export-data-columns-dialogue.component";
import { DatePipe } from "@angular/common";
import { forkJoin } from "rxjs";
import { MatSelect } from "@angular/material/select";
import { TranslateService } from "@ngx-translate/core";
import { columns } from "../hold-item-list/holdItemListColums";
import { MatSnackBar } from "@angular/material/snack-bar";

export type GridColumn = { id: string; columns: DisplayedColumn[]; groupedColumns?: { id: any; columns: DisplayedColumn[] }[] };

export enum DataFilter {
    All = 0,
    Original = 1,
    Edited = 2,
}
@Component({
    selector: "app-export-data",
    templateUrl: "./export-data.component.html",
    styleUrls: ["./export-data.component.scss"],
    providers: [AccessValuePipe, DatePipe],
    standalone: false,
})
export class ExportDataComponent implements OnInit {
    private readonly _bootstrapService = inject(BootstrapService);

    protected readonly Number = Number;
    protected readonly DataFilter = DataFilter;

    @ViewChild("gridComponent") gridComponent: GridComponent;
    @ViewChild("searchInput") searchInput: ElementRef;
    @ViewChild("startDateInput") startDateInput: ElementRef;
    @ViewChild("endDateInput") endDateInput: ElementRef;

    public displayedColumns: DisplayedColumn[] = allExportFlightColumns.slice();
    public gridColumns: GridColumn[] = [];
    public flightsFilterForm: FormGroup;

    public helicopterList: Helicopter[] = [];
    public blocksList = [];
    public blockOptions: { value: number; label: string }[] = [];

    public currentHelicopter: Helicopter;
    public currentBlockId: number;

    private _userSession: UserLocalStorageObject;
    private columnsTitles = columnTitlesMap;

    public ignoreChanges = false;

    public currentDataFilter = DataFilter.All;
    public exportData = [];

    public minDate = null;
    public maxDate = null;
    public helicopterChosen = false;

    public gridView: GridDataResult;
    public originalData: any[] = [];
    public data: any[] = [];

    public pageSizes = [25, 50, 100, 200];

    public locationFilterActive = false;
    public locationColGroup: ColumnComponent = {
        field: "locationFilter",
        title: "Fuel Depot",
        filterable: true,
        filter: "text",
    } as unknown as ColumnComponent;

    @ViewChild("helicopterSelect") helicopterSelect: MatSelect;

    public state: State = {
        skip: 0,
        take: this.pageSizes[0],
        sort: [
            {
                field: "flightId",
                dir: "asc",
            },
        ],
    };

    public today: Date;

    public isAdmin = false;
    public hasPresetFilters = false;
    public presetFilterOptions: any[] = [];

    constructor(
        private _htlService: HtlService,
        private _flightsService: FlightsService,
        private _translate: TranslateService,
        private _helicoptersService: HelicoptersService,
        private _dialog: MatDialog,
        private _snackbar: MatSnackBar,
        private _localStorageService: LocalStorageService,
        private _formBuilder: FormBuilder,
    ) {
        this.today = new Date();
        this._userSession = this._localStorageService.getUserLocalStorageObject();
        this.flightsFilterForm = this._formBuilder.group({
            helicopters: new FormControl([], [Validators.required]),
            blockId: [Validators.required],
            startDate: [],
            endDate: this.today,
            presetFilter: [-1],
        });
    }

    ngOnInit() {
        this.configDisplayedColumns();

        if (this._userSession.htlAccountTypes.length > 0) {
            switch (this._userSession.htlAccountTypes[0].id) {
                case 1:
                    this.isAdmin = true;
                    break;
                case 2:
                    this.isAdmin = true;
                    break;
                case 3:
                    this.isAdmin = true;
                    break;
            }
        }

        this.hasPresetFilters = this._userSession.appConfigs.find((c) => c.type === AppConfigType.exportPresetFilters)?.enabled || false;

        if (this.hasPresetFilters) {
            this.presetFilterOptions = this._userSession.exportPresetFilters;
        }

        this.flightsFilterForm.controls["blockId"].disable({ emitEvent: false });
        this.flightsFilterForm.controls["startDate"].disable({ emitEvent: false });
        this.flightsFilterForm.controls["endDate"].disable({ emitEvent: false });

        if (this._userSession.displayedColumns) {
            this.displayedColumns = this._userSession.displayedColumns;
        }

        // Hardcoded for Starworksky and Avcon
        if (this._userSession.organizationId === 8 || this._userSession.organizationId === 30) {
            this.displayedColumns.filter((dc) => dc.def === "cycles")[0].label = "Landings";
            this.displayedColumns.filter((dc) => dc.def === "totalCycles")[0].label = "A/C Cycles";
        }

        this._helicoptersService.getHelicopters(this._userSession.organizationId).subscribe({
            next: (helicopters) => {
                this.helicopterList = helicopters;
                helicopters.forEach((h) => {
                    this._htlService.getHelicopterHtlBlocks(h.id.toString()).subscribe({
                        next: (blocks) => {
                            blocks.forEach((b) => {
                                this.blocksList.push({
                                    id: b.id,
                                    helicopterId: h.id,
                                    serialNumber: b.blockSerialNumber,
                                    issueDate: b.issueDate,
                                    helicopterType: b.helicopterType,
                                });
                            });
                        },
                        error: () => console.log(`Unable to fetch blocks of helicopter ${h.registrationMark}`),
                    });
                });
            },
            error: () => console.log("Unable to fetch helicopters"),
        });

        this.flightsFilterForm.valueChanges.subscribe(() => {
            if (this.ignoreChanges) {
                this.ignoreChanges = false;
                return;
            }
            if (this.flightsFilterForm.controls["startDate"].value && this.flightsFilterForm.controls["endDate"].value) {
                this.exportFlights();
            }
        });
    }

    private loadItems() {
        this.gridView = process(this.data, this.state);
    }

    public dataStateChange(state: DataStateChangeEvent): void {
        this.state = state;
        this.loadItems();
    }

    private mapResponseToData(response: any) {
        const paxEnabled = this._userSession.appConfigs.find((c) => c.type === AppConfigType.passengers)?.enabled || false;
        const crewEnabled = this._userSession.appConfigs.find((c) => c.type === AppConfigType.crew)?.enabled || false;
        const hooksEnabled = this._userSession.appConfigs.find((c) => c.type === AppConfigType.hooks)?.enabled || false;
        const hoistEnabled = this._userSession.appConfigs.find((c) => c.type === AppConfigType.hoist)?.enabled || false;
        const massBalanceCodeEnabled = this._userSession.appConfigs.find((c) => c.type === AppConfigType.massBalanceCode)?.enabled || false;

        return response.map((r: any) => {
            const res = {
                ...r,
                totalFuelBeforeNextFlight: r.totalFuelBeforeNextFlight?.toString() === "0" ? null : r.totalFuelBeforeNextFlight,
                fuelArrival: r.fuelArrival?.toString() === "0" ? null : r.fuelArrival,
                refuel: r.refuel?.toString() === "0" ? null : r.refuel,
                totalFuel: r.totalFuel?.toString() === "0" ? null : r.totalFuel,
                refuelBeforeFirstFlight: r.refuelBeforeFirstFlight?.toString() === "0" ? null : r.refuelBeforeFirstFlight,
                totalFuelBeforeNextFlightUnit: r.totalFuelBeforeNextFlight?.toString() === "0" ? null : r.totalFuelBeforeNextFlightUnit,
                fuelArrivalUnit: r.fuelArrival?.toString() === "0" ? null : r.fuelArrivalUnit,
                refuelUnit: r.refuel?.toString() === "0" ? null : r.refuelUnit,
                totalFuelUnit: r.totalFuel?.toString() === "0" ? null : r.totalFuelUnit,
                refuelBeforeFirstFlightUnit: r.refuelBeforeFirstFlight?.toString() === "0" ? null : r.refuelBeforeFirstFlightUnit,
            };
            if (!paxEnabled) {
                delete res.passengers;
                delete res.payingPassengers;
                delete res.isTax;
                delete res.taxDistance;
            } else {
                const payingPassengers = !isNaN(r.payingPassengers) ? Number(r.payingPassengers) : undefined;
                if (payingPassengers > 0) {
                    if (res.isTax) {
                        res.taxPassengers = payingPassengers;
                        delete res.payingPassengers;
                    }
                }
            }

            if (crewEnabled && r.crewData && r.crewData?.length > 0) {
                res.crew = r.crewData.map((c) => c.firstName + (c.lastName ? ` ${c.lastName}` : ""));
                if (res.crew?.length > 1) res.crew = res.crew.join("\n");

                const crewRoles = this._bootstrapService.crewRoles();
                res.crewRole = r.crewData.map((c) => {
                    return crewRoles.find((r) => r.id === c.crewRoleId)?.name ?? "Unknown";
                });

                if (res.crewRole?.length > 1) res.crewRole = res.crewRole.join("\n");
            }

            if (!hooksEnabled) {
                delete res?.hooks;
                delete res?.hook;
            } else {
                if (res.hooks) {
                    res.hook = {
                        ...res.hook,
                        cycles: res.hooks,
                    };
                }
            }

            if (!hoistEnabled) {
                delete res.hoist;
            }

            if (!massBalanceCodeEnabled) {
                delete res["massBalanceCode"];
            }

            return res;
        });
    }

    private configPassengersColumns() {
        const paxConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.passengers);
        let paxEnabled = false;
        let payingPaxEnabled = false;
        let taxEnabled = false;
        let taxDistance: string;
        if (paxConfig) {
            paxEnabled = paxConfig.enabled;
            const values = paxConfig?.value?.length > 0 ? paxConfig.value.trim().split("-") : undefined;
            if (values?.length > 0) {
                payingPaxEnabled = values.length > 0 && values[0] === "1";
                if (values.length > 1) {
                    taxEnabled = values[1].length > 0 && values[1] !== "0";
                    if (values[1].length > 0) taxDistance = values[1];
                    if (values.length > 2) {
                        if (values[2].length > 0) taxDistance += values[2];
                    }
                }
            }
        }

        const cols = this.displayedColumns;

        if (!paxEnabled) {
            const paxIndex = cols.findIndex((c) => c.def === "passengers");
            if (paxIndex != -1) cols.splice(paxIndex, 1);
            const payingIndex = cols.findIndex((c) => c.def === "payingPassengers");
            if (payingIndex != -1) cols.splice(payingIndex, 1);
            const taxIndex = cols.findIndex((c) => c.def === "taxPassengers");
            if (taxIndex != -1) cols.splice(taxIndex, 1);
        } else {
            if (!payingPaxEnabled) {
                const paxIndex = cols.findIndex((c) => c.def === "passengers");
                if (paxIndex != -1) delete cols[paxIndex].subgroup;
                const payingIndex = cols.findIndex((c) => c.def === "payingPassengers");
                if (payingIndex != -1) cols.splice(payingIndex, 1);
                const taxIndex = cols.findIndex((c) => c.def === "taxPassengers");
                if (taxIndex != -1) cols.splice(taxIndex, 1);
            } else {
                if (!taxEnabled) {
                    const taxIndex = cols.findIndex((c) => c.def === "taxPassengers");
                    if (taxIndex != -1) cols.splice(taxIndex, 1);
                } else {
                    const payingIndex = cols.findIndex((c) => c.def === "payingPassengers");
                    const taxIndex = cols.findIndex((c) => c.def === "taxPassengers");
                    if (taxIndex != -1 && payingIndex != -1) {
                        cols[payingIndex].label = `Paying Pax <${taxDistance}`;
                        cols[taxIndex].label = `Paying Pax >${taxDistance}`;
                    }
                }
            }
        }

        this.displayedColumns = cols;
    }

    private configPOBColumns() {
        const pobConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.pob);
        const pobEnabled = pobConfig?.enabled || false;
        if (pobEnabled) {
            this.columnsTitles.set("pax", "POB");
            const paxIndex = this.displayedColumns.findIndex((c) => c.def === "passengers");
            if (paxIndex != -1) {
                this.displayedColumns[paxIndex].label = "POB";
            }
        }
    }

    private configCrewColumns() {
        const crewConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.crew);
        const crewEnabled = crewConfig?.enabled || false;
        if (!crewEnabled) {
            const crewIndex = this.displayedColumns.findIndex((c) => c.def === "crew");
            if (crewIndex != -1) this.displayedColumns.splice(crewIndex, 1);
            const crewRoleIndex = this.displayedColumns.findIndex((c) => c.def === "crewRole");
            if (crewRoleIndex != -1) this.displayedColumns.splice(crewRoleIndex, 1);
        }
    }

    private configRefuelBeforeFirstFlight() {
        if (this._localStorageService.getUserLocalStorageObject().htlAccountTypes.length <= 0) {
            const refuelBeforeFirstFlightDateIndex = this.displayedColumns.findIndex((c) => c.def === "refuelBeforeFirstFlightDate");
            if (refuelBeforeFirstFlightDateIndex != -1) this.displayedColumns.splice(refuelBeforeFirstFlightDateIndex, 1);
        }
    }

    private configRefuelLocations() {
        const locationConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.flightRefuelLocation);
        const locationEnabled = locationConfig?.enabled || false;
        if (!locationEnabled) {
            const firstRefuelLocationIndex = this.displayedColumns.findIndex((c) => c.def === "firstRefuelLocation");
            if (firstRefuelLocationIndex != -1) this.displayedColumns.splice(firstRefuelLocationIndex, 1);
            const secondRefuelLocationIndex = this.displayedColumns.findIndex((c) => c.def === "secondRefuelLocation");
            if (secondRefuelLocationIndex != -1) this.displayedColumns.splice(secondRefuelLocationIndex, 1);
        } else {
            if (JSON.parse(locationConfig.value).label === "fuel_depot") {
                this.columnsTitles.set("location", "FUEL DEPOT");
            }
        }
    }

    private configPenaltyFactorColumns() {
        const penaltyConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.penaltyFactor);
        const penaltyEnabled = penaltyConfig?.enabled || false;
        if (!penaltyEnabled) {
            const penaltyIndex = this.displayedColumns.findIndex((c) => c.def === "penality");
            if (penaltyIndex != -1) this.displayedColumns.splice(penaltyIndex, 1);
        }
    }

    private configPositioningFlight() {
        const positioningConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.positioningFlight);
        const positioningEnabled = positioningConfig?.enabled || false;
        if (!positioningEnabled) {
            const positioningIndex = this.displayedColumns.findIndex((c) => c.def === "isPositioning");
            if (positioningIndex != -1) this.displayedColumns.splice(positioningIndex, 1);
        }
    }

    private configEnginePowerCheckColumns() {
        const enginePowerCheckConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.enginePowerCheck);
        const enginePowerCheckEnabled = enginePowerCheckConfig?.enabled || false;
        if (!enginePowerCheckEnabled) {
            const enginePowerCheckIndexes = this.displayedColumns.filter((c) => c.def.includes("enginePowerCheck"));
            for (const e of enginePowerCheckIndexes) {
                const index = this.displayedColumns.findIndex((c) => c.def === e.def);
                if (index != -1) this.displayedColumns.splice(index, 1);
            }
        }
    }

    private configHookColumn() {
        const hookConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.hooks);
        const hookEnabled = hookConfig?.enabled || false;
        if (!hookEnabled) {
            const hookIndexes = this.displayedColumns.filter((c) => c.def.includes("hook"));
            for (const h of hookIndexes) {
                const index = this.displayedColumns.findIndex((c) => c.def === h.def);
                if (index != -1) this.displayedColumns.splice(index, 1);
            }
        } else {
            const value = JSON.parse(hookConfig.value);
            const cyclesIndex = this.displayedColumns.findIndex((c) => c.def === "hook.cycles");
            if (cyclesIndex != -1) this.displayedColumns[cyclesIndex].label = value.label;
            if (!value.hours) {
                const hoursIndex = this.displayedColumns.findIndex((c) => c.def === "hook.hours");
                if (hoursIndex != -1) this.displayedColumns.splice(hoursIndex, 1);
            }
        }
    }

    private configHoistColumn() {
        const hoistConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.hoist);
        const hoistEnabled = hoistConfig?.enabled || false;
        if (!hoistEnabled) {
            const hoistIndexes = this.displayedColumns.filter((c) => c.def.includes("hoist"));
            for (const h of hoistIndexes) {
                const index = this.displayedColumns.findIndex((c) => c.def === h.def);
                if (index != -1) this.displayedColumns.splice(index, 1);
            }
        } else {
            const value = JSON.parse(hoistConfig.value);
            if (!value.cycles) {
                const cyclesIndex = this.displayedColumns.findIndex((c) => c.def === "hoist.cycles");
                if (cyclesIndex != -1) this.displayedColumns.splice(cyclesIndex, 1);
            }
            if (!value.hours) {
                const hoursIndex = this.displayedColumns.findIndex((c) => c.def === "hoist.hours");
                if (hoursIndex != -1) this.displayedColumns.splice(hoursIndex, 1);
            }
        }
    }

    private configEngineForms() {
        const engineFormsConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.enginesForm);
        const { cycles, hydraulics, drivetrain } = JSON.parse(engineFormsConfig.value);
        if (!engineFormsConfig.enabled || !cycles) {
            const cyclesIndexes = this.displayedColumns.filter((c) => c.def.includes("helicopter.engine"));
            for (const i of cyclesIndexes) {
                const index = this.displayedColumns.findIndex((c) => c.def === i.def);
                if (index != -1) this.displayedColumns.splice(index, 1);
            }
        }
        if (!engineFormsConfig.enabled || !hydraulics) {
            const hydraulicsIndexes = this.displayedColumns.filter((c) => c.def.includes("hyd"));
            for (const h of hydraulicsIndexes) {
                const index = this.displayedColumns.findIndex((c) => c.def === h.def);
                if (index != -1) this.displayedColumns.splice(index, 1);
            }
        }
        if (!engineFormsConfig.enabled || !drivetrain) {
            const drivetrainIndexes = this.displayedColumns.filter((c) => c.def.includes("tgb") || c.def.includes("mgb"));
            for (const d of drivetrainIndexes) {
                const index = this.displayedColumns.findIndex((c) => c.def === d.def);
                if (index != -1) this.displayedColumns.splice(index, 1);
            }
        }
    }

    private configFlightNumber() {
        const fnConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.flightNumber);
        const fnEnabled = fnConfig?.enabled || false;
        if (!fnEnabled) {
            const fnIndex = this.displayedColumns.findIndex((c) => c.def === "flightNumber");
            if (fnIndex != -1) this.displayedColumns.splice(fnIndex, 1);
        }
    }

    private configMassBalanceCode() {
        const mbcConfig = this._userSession.appConfigs.find((c) => c.type === AppConfigType.massBalanceCode);
        const mbcEnabled = mbcConfig?.enabled || false;
        if (!mbcEnabled) {
            const mbcIndex = this.displayedColumns.findIndex((c) => c.def === "massBalanceCode");
            if (mbcIndex != -1) this.displayedColumns.splice(mbcIndex, 1);
        }
    }

    private configDisplayedColumns() {
        this.configPassengersColumns();
        this.configPOBColumns();
        this.configCrewColumns();
        this.configRefuelBeforeFirstFlight();
        this.configRefuelLocations();
        this.configPenaltyFactorColumns();
        this.configPositioningFlight();
        this.configEnginePowerCheckColumns();
        this.configHookColumn();
        this.configHoistColumn();
        this.configEngineForms();
        this.configFlightNumber();
        this.configMassBalanceCode();

        const groupKeys: string[] = Array.from(new Set(this.displayedColumns.filter((column) => column.group).map((column) => column.group)));

        const subgroupsKeys: string[] = Array.from(new Set(this.displayedColumns.filter((column) => column.subgroup).map((column) => column.subgroup)));

        const gridColumns = groupKeys.map<GridColumn>((gKey) => ({
            id: gKey,
            columns: this.displayedColumns.filter((dc) => dc.group === gKey),
            groupedColumns: [],
        }));

        gridColumns.forEach((group) => {
            const subgroup: { id: any; columns: any[] }[] = [];
            for (let i = 0; i < group.columns.length; i++) {
                const dc = group.columns[i];
                if (!dc.subgroup) {
                    if (isNaN(subgroup[subgroup.length - 1]?.id)) subgroup.push({ id: i, columns: [] });
                } else if (subgroupsKeys.includes(dc.subgroup)) {
                    if (!subgroup.some((subgroup) => subgroup.id === dc.subgroup)) {
                        subgroup.push({ id: dc.subgroup, columns: [] });
                    }
                }
                const lastGroup = subgroup[subgroup.length - 1];
                lastGroup.columns.push(dc);
            }
            group.groupedColumns = subgroup;
        });

        gridColumns.forEach((obj) => {
            if (obj.groupedColumns.length === 1 && obj.groupedColumns[0].columns.length === obj.columns.length) {
                obj.groupedColumns = undefined;
            }
        });

        this.gridColumns = gridColumns;
    }

    /**
     * Handles the Aircraft Select menu.
     * Once an Aircraft is selected, enable block number select and date selects, to further filter the data
     *
     * Multiple Aircrafts can be selected, in which case it should not be possible to select a block number
     * Additionally the selectable date range for multiple aircrafts should be a month, so once a date is set
     * the minimum should be the first day of the month and maximum should be the last day.
     *
     * @param event event result for handling select
     * @param helicopterId id of helicopter for which selection was changed
     */

    public onHelicopterChosen(event: any, helicopterId: number) {
        if (!event.isUserInput) return;
        this.helicopterChosen = false;

        const registrationMarks = [];
        this.helicopterList.forEach((h) => {
            if (this.flightsFilterForm.controls["helicopters"].value.includes(h.id)) {
                if (!(helicopterId === h.id && !event.source._selected)) {
                    registrationMarks.push(h.registrationMark);
                    this.helicopterChosen = true;
                }
            }
            if (h.id === helicopterId && event.source._selected) {
                registrationMarks.push(h.registrationMark);
                this.helicopterChosen = true;
            }
        });
        this.helicopterSelect.trigger.nativeElement.innerText = registrationMarks.join(", ");

        this.minDate = null;
        this.maxDate = null;
        this.data = [];
        this.blockOptions = [];
        this.ignoreChanges = true;
        this.flightsFilterForm.get("blockId").setValue(undefined);
        this.flightsFilterForm.get("startDate").setValue(undefined);
        this.flightsFilterForm.get("endDate").setValue(this.today);

        this.currentHelicopter = this.helicopterList.find((h) => h.id === helicopterId);
        this.currentBlockId = undefined;

        if (
            (event.source._selected && this.flightsFilterForm.controls["helicopters"].value.length <= 0) ||
            (!event.source._selected && this.flightsFilterForm.controls["helicopters"].value.length === 2)
        ) {
            this.flightsFilterForm.controls["blockId"].enable({ onlySelf: true, emitEvent: false });
            this.flightsFilterForm.controls["startDate"].enable({ onlySelf: true, emitEvent: false });
            this.flightsFilterForm.controls["endDate"].enable({ onlySelf: true, emitEvent: false });

            if (!event.source._selected) {
                helicopterId = this.flightsFilterForm.controls["helicopters"].value.filter((h) => h !== helicopterId)[0];
            }

            this.blockOptions = this.blocksList
                .filter((bl) => bl.helicopterId === helicopterId)
                .map((bl) => {
                    return { value: bl.id, label: "Block Paper Nr. " + bl.serialNumber.toString() + " - " + bl.helicopterType };
                })
                .reverse();
        } else {
            this.flightsFilterForm.controls["blockId"].disable({ emitEvent: false });
            this.flightsFilterForm.controls["startDate"].enable({ emitEvent: false });
            this.flightsFilterForm.controls["endDate"].enable({ emitEvent: false });
            this.flightsFilterForm.controls["startDate"].setValue(undefined);
            this.flightsFilterForm.controls["endDate"].setValue(undefined);
        }
    }

    /**
     * Handles the select all button in the top of the aircraft select menu
     * When this option is selected it should populate the form with all available aircrafts
     * When it is deselected it should empty out the form
     *
     * Also updates the fields accordingly and manually writes values for mat select
     * as it does not update checkboxes automatically if changing form values
     */

    public onAllHelicoptersChosen(event) {
        this.helicopterChosen = false;
        this.helicopterSelect.trigger.nativeElement.innerText = this._translate.instant("FLIGHTS.TABLE.helicopter");
        this.flightsFilterForm.controls["helicopters"].setValue([]);
        this.flightsFilterForm.controls["blockId"].disable({ emitEvent: false });
        this.flightsFilterForm.controls["startDate"].disable({ emitEvent: false });
        this.flightsFilterForm.controls["endDate"].disable({ emitEvent: false });
        if (event.source._selected) {
            this.helicopterChosen = true;
            this.flightsFilterForm.controls["startDate"].enable({ emitEvent: false });
            this.flightsFilterForm.controls["endDate"].enable({ emitEvent: false });
            this.flightsFilterForm.controls["startDate"].setValue(undefined);
            this.flightsFilterForm.controls["endDate"].setValue(undefined);
            this.helicopterList.forEach((h) => {
                this.flightsFilterForm.controls["helicopters"].value.push(h.id);
            });
            this.helicopterSelect.trigger.nativeElement.innerText = this._translate.instant("EXPORT.allAircrafts");
        }
        this.helicopterSelect.writeValue(this.flightsFilterForm.controls["helicopters"].value);
        this.flightsFilterForm.updateValueAndValidity();
    }

    public onBlockChosen(event: any, blockId?: number) {
        if (!event.isUserInput) return;

        const hId = this.flightsFilterForm.get("helicopters").value[0];

        if (!hId || !blockId) {
            this.data = null;
            return;
        }

        this.currentBlockId = blockId;

        this.exportFlights();
    }

    public onDateChosen(value) {
        if (this.flightsFilterForm.controls["helicopters"].value.length <= 1) return;
        if (
            (this.flightsFilterForm.controls["startDate"].value === null || this.flightsFilterForm.controls["startDate"].value === undefined) &&
            (this.flightsFilterForm.controls["endDate"].value === null || this.flightsFilterForm.controls["endDate"].value === undefined)
        ) {
            this.minDate = null;
            this.maxDate = null;
            return;
        }
        if (value === null) return;
        const date = new Date(value);
        this.minDate = new Date(date.getFullYear(), date.getMonth());
        this.maxDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        if (this.maxDate > this.today) {
            this.maxDate = this.today;
        }
    }

    public onPresetFilterChosen(event, filter?) {
        if (!event.isUserInput) {
            return;
        }

        this.gridColumns.forEach((c) => {
            if (c.groupedColumns) {
                c.groupedColumns.forEach((gc) => {
                    gc.columns.forEach((col) => {
                        if (!filter || filter.columns.find((fcol) => fcol.columnName === col.def)) {
                            col.hidden = false;
                        } else {
                            col.hidden = true;
                        }
                    });
                });
            } else {
                c.columns.forEach((col) => {
                    if (!filter || filter.columns.find((fcol) => fcol.columnName === col.def)) {
                        col.hidden = false;
                    } else {
                        col.hidden = true;
                    }
                });
            }
        });

        if (filter?.type === 2) {
            this.state.group = [
                {
                    field: "pilot1Name",
                    dir: "asc",
                },
            ];
        } else {
            this.state.group = [];
        }
    }

    public exportFlights() {
        this.data = [];
        const exports = [];
        let startDate;
        if (this.flightsFilterForm.controls["startDate"].value) {
            startDate = new Date(this.flightsFilterForm.controls["startDate"].value);
        }
        if (startDate) {
            startDate.setHours(0, 0, 0, 0);
        }
        let endDate;
        if (this.flightsFilterForm.controls["endDate"].value) {
            endDate = new Date(this.flightsFilterForm.controls["endDate"].value);
        }
        if (endDate) {
            endDate.setHours(23, 59, 59, 999);
        }
        this.flightsFilterForm.controls["helicopters"].value.forEach((hId: number) => {
            if (hId !== -1) {
                exports.push(
                    this._flightsService.exportFlights(
                        hId,
                        this.currentBlockId ? this.currentBlockId : -1,
                        startDate ? startDate.toISOString() : null,
                        endDate ? endDate.toISOString() : null,
                    ),
                );
            }
        });
        forkJoin(exports).subscribe({
            next: (res: any) => {
                res.forEach((r) => {
                    console.log(r);
                    this.data = this.data.concat(this.mapResponseToData(r));
                    this.originalData = this.data;
                });
                this.state.skip = 0;
                this.loadItems();
            },
            error: (err) => console.log(err),
        });
    }

    public exportToExcel(grid: GridComponent) {
        const helicopters = this.flightsFilterForm.get("helicopters").value.length;
        const bId = this.flightsFilterForm.get("blockId").value;
        const startDate = this.flightsFilterForm.get("startDate").value;
        const endDate = this.flightsFilterForm.get("endDate").value;
        if (helicopters <= 0) {
            return alert("Please select a helicopter");
        }
        if (isNaN(bId) && (isNaN(startDate) || isNaN(endDate))) {
            return alert("Please select a block or date range");
        }

        const hasHidden = this.gridColumns.some(
            (gc) => gc?.columns.some((c) => c?.hidden === true) || false || gc?.groupedColumns?.some((c) => c?.columns?.some((c) => c?.hidden === true) || false) || false,
        );

        if (!hasHidden || this.flightsFilterForm.controls["presetFilter"].value !== -1) return grid.saveAsExcel();

        const dialogRef = this._dialog.open(ExportDataDialogueComponent, {
            disableClose: true,
            width: "300px",
            data: {},
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) grid.saveAsExcel();
        });
    }

    public configureExport = (): ExcelExportData => {
        const state: State = {
            group: this.state.group,
            sort: this.state.sort,
            filter: this.state.filter,
        };

        this.exportData = this.data;

        if (this.currentDataFilter === DataFilter.Original) {
            this.exportData = this.data.filter((d) => (!d.isEdited && this.data.filter((f) => f.id === d.id).length < 2) || d.isEdited);
        }
        if (this.currentDataFilter === DataFilter.Edited) {
            this.exportData = this.data.filter((d) => d.isEdited);
        }

        const processedData = process(this.exportData, state).data;

        const crewEnabled = this._userSession.appConfigs.find((c) => c.type === AppConfigType.crew)?.enabled || false;

        processedData.forEach((r) => {
            if (crewEnabled && r.crewData && r.crewData?.length > 0) {
                r.crew = r.crewData.map((c) => c.firstName + (c.lastName ? ` ${c.lastName}` : ""));
                r.crew = r.crew.join("\n");

                const crewRoles = this._bootstrapService.crewRoles();
                r.crewRole = r.crewData.map((c) => {
                    return crewRoles.find((r) => r.id === c.crewRoleId)?.name ?? "Unknown";
                });

                r.crewRole = r.crewRole.join("\n");
            }
        });

        return {
            data: processedData,
            group: this.state.group,
        };
    };

    public onExcelExport(e: ExcelExportEvent) {
        const sheet = e.workbook.sheets[0];
        const blue = "#4050b5";
        sheet.formats = [];

        const rows = sheet.rows;
        const headers = rows.filter((r) => r.type === "header");
        const groupHeaders = rows.filter((r) => r.type === "group-header");

        const groups = headers[0];
        groups.cells.forEach((cell) => {
            cell.borderLeft = { ...cell.borderLeft, size: 2, color: "#ffffff" };
            cell.borderTop = { ...cell.borderTop, size: 2, color: "#ffffff" };
            cell.borderRight = { ...cell.borderRight, size: 2, color: "#ffffff" };
            if (cell.value) cell.borderBottom = { ...cell.borderBottom, size: 2, color: "#ffffff" };
        });
        const subgroups = headers[1];
        subgroups.cells.forEach((cell) => {
            cell.borderLeft = { ...cell.borderLeft, size: 2, color: "#ffffff" };
            cell.borderRight = { ...cell.borderRight, size: 2, color: "#ffffff" };
        });
        const colHeaders = headers[2];
        colHeaders.cells.forEach((cell) => {
            cell.hAlign = "center";
            cell.background = blue;
            cell.borderLeft = { ...cell.borderLeft, size: 2, color: "#ffffff" };
            cell.borderRight = { ...cell.borderRight, size: 2, color: "#ffffff" };
            cell.borderBottom = { ...cell.borderBottom, size: 2, color: "#ffffff" };
        });
        groupHeaders.forEach((gh) => {
            gh.cells.forEach((cell) => {
                cell.color = "#ffffff";
                cell.background = blue;
            });
        });

        const dataRows = rows.filter((r) => r.type === "data");
        const red = "#e85f5f";
        const green = "#6ef46a";
        const yellow = "#f6ff93";

        let altIdx = 0;
        dataRows.forEach((row, i) => {
            if (altIdx % 2 !== 0) {
                row.cells.forEach((cell) => (cell.background = "#eef6ff"));
            } else {
                row.cells.forEach((cell) => (cell.background = "#ffffff"));
            }
            if (this.currentDataFilter === DataFilter.All) {
                if (row.cells.some((cell) => cell.value?.toString().includes("|"))) {
                    if (i > 0)
                        dataRows[i - 1].cells.forEach((cell, j) => {
                            if (i > 0 && dataRows[i].cells[j].value?.toString().includes("|")) {
                                cell.background = red;
                            } else {
                                cell.background = yellow;
                            }
                        });
                    row.cells.forEach((cell) => (cell.background = green));
                }
            }
            row.cells.forEach((cell) => {
                let value = cell?.value;
                if (value?.toString().includes("|")) cell.value = this.extractTextAfterPipe(value.toString());
                value = cell?.value;
                if (!isNaN(value as any)) {
                    if (cell.format?.includes("d")) {
                        cell.value = new Date(cell.value as number);
                    } else {
                        cell.value = Number(cell.value) === 0 ? "" : Number(cell.value);
                    }
                }
                if (cell.value === "") cell.value = undefined;
                if ((cell.format === "[h]:mm" || cell.format === "HH:mm") && value) {
                    const parts = value.split(":").map((p) => parseInt(p.trim(), 10));

                    const hours = parts[0];
                    const minutes = parts[1];

                    cell.value = (hours * 3600 + minutes * 60) / (24 * 60 * 60);
                }
            });

            altIdx++;
        });

        const footers = rows.filter((r) => r.type === "footer");
        footers.forEach((row) =>
            row.cells.forEach((cell) => {
                if (!isNaN(cell.value as any)) {
                    cell.value = Number(cell.value) === 0 ? undefined : Number(cell.value);
                } else {
                    cell.value = undefined;
                }
            }),
        );

        if (this.currentDataFilter === DataFilter.All) {
            rows.push(
                {},
                {},
                {
                    cells: [
                        { value: "Notes:", bold: true },
                        { value: "Original data should be ignored or removed from the sum calculation and only its updated version should be used" },
                    ],
                },
                {},
                { cells: [{ value: "LEGEND", bold: true }] },
                { cells: [{ background: yellow }, { value: "Original data flight information line" }] },
                { cells: [{ background: red }, { value: "Modified fields" }] },
                { cells: [{ background: green }, { value: "Updated/Corrected data flight information line" }] },
            );
        }
    }

    private extractTextAfterPipe(input: string): string {
        const parts = input.split("|");
        if (parts.length > 1) {
            let result = parts[1].trimStart();
            if (result.startsWith(" ")) {
                result = result.substring(1);
            }
            return result;
        }
        return input;
    }

    public onSearch(event: any) {
        const inputValue = event?.target?.value;
        const fields = Array.from(
            new Set([...this.gridColumns.flatMap((gd) => gd.columns), ...this.gridColumns.flatMap((gd) => gd.groupedColumns?.flatMap((gc) => gc.columns || []))]),
        );
        this.gridView = process(this.data, {
            ...this.state,
            filter: {
                logic: "or",
                filters: fields
                    ?.filter((f) => f?.def)
                    .map((f) => ({
                        field: f.def,
                        operator: "contains",
                        value: inputValue,
                    })),
            },
        });
        this.state.skip = 0;
    }

    public onClearSearch() {
        this.searchInput.nativeElement.value = "";
        this.onSearch({ target: { value: "" } });
    }

    public filterEdited(element: any) {
        return this.data.filter((d) => d.id === element.id).length >= 2;
    }

    sumColumn(def: string, data: any[]) {
        if (!def || !data) return "";
        data = data.filter((d) => {
            return (!d.isEdited && data.filter((f) => f.id === d.id).length === 1) || d.isEdited;
        });

        const getValueIfEdited = (d: any) => {
            if (d?.isEdited && d.includes("|")) {
                const edited = d.toString().split("|");
                if (edited.length === 2) return edited[1].trim();
            }
            return d;
        };

        if (def === "startStopTimeMin") {
            return data
                ?.filter((d) => d.startStopTimeMin)
                ?.map((d) => getValueIfEdited(d.startStopTimeMin))
                ?.reduce((acc, val) => acc + val, 0);
        }
        if (def === "startStopTime") {
            return this.minutesToHHmm(
                data
                    ?.filter((d) => d.startStopTimeMin)
                    ?.map((d) => getValueIfEdited(d.startStopTimeMin))
                    ?.reduce((acc, val) => acc + val, 0),
            );
        }
        if (def === "flightTimeMin") {
            return data
                ?.filter((d) => d.flightTimeMin)
                ?.map((d) => getValueIfEdited(d.flightTimeMin))
                ?.reduce((acc, val) => acc + val, 0);
        }
        if (def === "flightTime") {
            return this.minutesToHHmm(
                data
                    ?.filter((d) => d.flightTimeMin)
                    ?.map((d) => getValueIfEdited(d.flightTimeMin))
                    ?.reduce((acc, val) => acc + val, 0),
            );
        }
        if (def === "refuelBeforeFirstFlight") {
            const unit = new Set(data?.filter((d) => d.refuelBeforeFirstFlightUnit)?.map((d) => getValueIfEdited(d.refuelBeforeFirstFlightUnit)));
            if (unit.size === 1) {
                return data
                    ?.filter((d) => !isNaN(getValueIfEdited(d.refuelBeforeFirstFlight)))
                    ?.map((d) => Number(getValueIfEdited(d.refuelBeforeFirstFlight)))
                    ?.reduce((acc, val) => acc + val, 0)
                    ?.toFixed(2);
            }
        }
        if (def === "refuelBeforeFirstFlightUnit") {
            const unit = new Set(data?.filter((d) => d.refuelBeforeFirstFlightUnit)?.map((d) => getValueIfEdited(d.refuelBeforeFirstFlightUnit)));
            if (unit.size === 1) return unit.values().next().value;
        }
        if (def === "refuel") {
            const unit = new Set(data?.filter((d) => d.refuelUnit)?.map((d) => getValueIfEdited(d.refuelUnit)));
            if (unit.size === 1) {
                return data
                    ?.filter((d) => !isNaN(getValueIfEdited(d?.refuel)))
                    ?.map((d) => Number(getValueIfEdited(d.refuel)))
                    ?.reduce((acc, val) => acc + val, 0)
                    ?.toFixed(2);
            }
        }
        if (def === "refuelUnit") {
            const unit = new Set(data?.filter((d) => d.refuelUnit && d.refuel?.toString() !== "0")?.map((d) => getValueIfEdited(d.refuelUnit)));
            if (unit.size === 1) return unit.values().next().value;
        }
        if (def === "oilSupplied.hyd") {
            const unit = new Set(data?.filter((d) => d.oilSupplied?.hydUnit)?.map((d) => getValueIfEdited(d.oilSupplied?.hydUnit)));
            if (unit.size === 1) {
                return data
                    ?.filter((d) => !isNaN(getValueIfEdited(d.oilSupplied?.hyd)))
                    ?.map((d) => Number(getValueIfEdited(d.oilSupplied.hyd)))
                    ?.reduce((acc, val) => acc + val, 0);
            }
        }
        if (def === "oilSupplied.hydUnit") {
            const unit = new Set(data?.filter((d) => d.oilSupplied?.hydUnit)?.map((d) => getValueIfEdited(d.oilSupplied?.hydUnit)));
            if (unit.size === 1) return unit.values().next().value;
        }
        if (def === "oilSupplied.mgb") {
            const unit = new Set(data?.filter((d) => d.oilSupplied?.mgbUnit)?.map((d) => getValueIfEdited(d.oilSupplied?.mgbUnit)));
            if (unit.size === 1) {
                return data
                    ?.filter((d) => !isNaN(getValueIfEdited(d.oilSupplied?.mgb)))
                    ?.map((d) => Number(getValueIfEdited(d.oilSupplied?.mgb)))
                    ?.reduce((acc, val) => acc + val, 0);
            }
        }
        if (def === "oilSupplied.mgbUnit") {
            const unit = new Set(data?.filter((d) => d.oilSupplied?.mgbUnit)?.map((d) => getValueIfEdited(d.oilSupplied?.mgbUnit)));
            if (unit.size === 1) return unit.values().next().value;
        }
        if (def === "oilSupplied.tgb") {
            const unit = new Set(data?.filter((d) => d.oilSupplied?.tgbUnit)?.map((d) => getValueIfEdited(d.oilSupplied?.tgbUnit)));
            if (unit.size === 1) {
                return data
                    ?.filter((d) => !isNaN(getValueIfEdited(d.oilSupplied?.tgb)))
                    ?.map((d) => Number(getValueIfEdited(d.oilSupplied?.tgb)))
                    ?.reduce((acc, val) => acc + val, 0);
            }
        }
        if (def === "oilSupplied.tgbUnit") {
            const unit = new Set(data?.filter((d) => d.oilSupplied?.tgbUnit)?.map((d) => getValueIfEdited(d.oilSupplied?.tgbUnit)));
            if (unit.size === 1) return unit.values().next().value;
        }

        if (def === "oilSupplied.engine1") {
            const unit = new Set(data?.filter((d) => d.oilSupplied?.engine1Unit)?.map((d) => getValueIfEdited(d.oilSupplied?.engine1Unit)));
            if (unit.size === 1) {
                return data
                    ?.filter((d) => !isNaN(getValueIfEdited(d.oilSupplied?.engine1)))
                    ?.map((d) => Number(getValueIfEdited(d.oilSupplied?.engine1)))
                    ?.reduce((acc, val) => acc + val, 0);
            }
        }

        if (def === "oilSupplied.engine1Unit") {
            const unit = new Set(data?.filter((d) => d.oilSupplied?.engine1Unit)?.map((d) => getValueIfEdited(d.oilSupplied?.engine1Unit)));
            if (unit.size === 1) return unit.values().next().value;
        }

        if (def === "oilSupplied.engine2") {
            const unit = new Set(data?.filter((d) => d.oilSupplied?.engine2Unit)?.map((d) => getValueIfEdited(d.oilSupplied?.engine2Unit)));
            if (unit.size === 1) {
                return data
                    ?.filter((d) => !isNaN(getValueIfEdited(d.oilSupplied?.engine2)))
                    ?.map((d) => Number(getValueIfEdited(d.oilSupplied?.engine2)))
                    ?.reduce((acc, val) => acc + val, 0);
            }
        }

        if (def === "oilSupplied.engine2Unit") {
            const unit = new Set(data?.filter((d) => d.oilSupplied?.engine2Unit)?.map((d) => getValueIfEdited(d.oilSupplied?.engine2Unit)));
            if (unit.size === 1) return unit.values().next().value;
        }

        return "";
    }

    private minutesToHHmm(minutes: number): string {
        if (!minutes) return "";

        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        return `${hours}:${remainingMinutes.toString().padStart(2, "0")}`;
    }

    public getFilterType(format?: string) {
        if (!format) return "text";
        if (format.includes("d")) return "date";
        if (format.includes("n")) return "numeric";
        return "text";
    }

    public getExcelFormat(format?: string) {
        if (format?.includes("d")) return "dd/mm/yy";
        if (format?.toString() === "n0") return "0;-0;;@";
        if (format?.toString() === "n2") return "0.00;-0.00;;@";
        return format || "@";
    }

    public hideColumnsBasedOnHelicopterData(def: string): boolean {
        if (def === "totalCycles") return !this.currentHelicopter?.sumHooksCycles;
        if (def.includes("engine2")) return !this.currentHelicopter?.hasEngine2;
        return false;
    }

    public hideSubgroupBasedOnHelicopterData(id: any): boolean {
        if (id === "e2" || id === "Δe2") return !this.currentHelicopter?.hasEngine2;
        return false;
    }

    public getColumnTitleById(id: string): string {
        return this.columnsTitles.get(id) || "";
    }

    openSettingsDialog(): void {
        const filterId = this.flightsFilterForm.controls["presetFilter"].value;
        const dialogRef = this._dialog.open(ExportDataColumnsDialogueComponent, {
            width: "700px",
            data: {
                gridColumns: this.gridColumns,
                currentHelicopter: this.currentHelicopter,
                hasPresetFilters: this.hasPresetFilters,
                presetFilter: filterId !== -1 ? this.presetFilterOptions.find((f) => f.id === filterId) : undefined,
            },
        });

        dialogRef.afterClosed().subscribe((res) => {
            if (res) {
                const filters = this._localStorageService.getUserLocalStorageObject().exportPresetFilters;
                const newFilters = [];
                if (res.newFilter) {
                    this._snackbar.open("Filter saved successfully", this._translate.instant("GENERAL.close"), {
                        duration: 3000,
                    });
                    let filterFound = false;
                    filters.forEach((f) => {
                        if (f.id === res.newFilter.id) {
                            newFilters.push(res.newFilter);
                            filterFound = true;
                        } else {
                            newFilters.push(f);
                        }
                    });
                    if (!filterFound) {
                        newFilters.push(res.newFilter);
                    }
                }

                if (res.filterDeleted) {
                    this._snackbar.open("Filter deleted successfully", this._translate.instant("GENERAL.close"), {
                        duration: 3000,
                    });
                    filters.forEach((f) => {
                        if (f.id !== res.filterDeleted.id) {
                            newFilters.push(f);
                        }
                    });
                }

                this._localStorageService.setUserLocalStorage("exportPresetFilters", newFilters);
                this._userSession = this._localStorageService.getUserLocalStorageObject();
                this.presetFilterOptions = this._userSession.exportPresetFilters;
                this.flightsFilterForm.controls["presetFilter"].setValue(res.newFilter ? res.newFilter.id : -1);
            }

            this._localStorageService.setUserLocalStorage("displayedColumns", this.displayedColumns);
        });
    }

    onSortChange(currentSort: SortDescriptor[]): void {
        const sort: SortDescriptor[] = [
            currentSort[0],
            {
                field: "flightId",
                dir: currentSort[0].dir,
            },
        ];
        this.state.sort = sort;
    }

    onFilterChange(filter: CompositeFilterDescriptor): void {
        const normalizedFilters = this.normalizeFilterGroup(filter);
        this.state.filter = this.processEmptyFilter(normalizedFilters);
    }

    private processEmptyFilter(filter: CompositeFilterDescriptor): CompositeFilterDescriptor {
        if (!filter || !filter.filters) {
            return filter;
        }

        const processedFilters: (FilterDescriptor | CompositeFilterDescriptor)[] = filter.filters.map((f) => {
            if ((f as CompositeFilterDescriptor).filters) {
                return this.processEmptyFilter(f as CompositeFilterDescriptor);
            }

            const currentFilter = f as FilterDescriptor;

            if (currentFilter.operator === "isnull") {
                return {
                    logic: "or",
                    filters: [
                        { field: currentFilter.field, operator: "eq", value: null },
                        { field: currentFilter.field, operator: "eq", value: "" },
                        { field: currentFilter.field, operator: "eq", value: undefined },
                        { field: currentFilter.field, operator: "eq", value: 0 },
                    ],
                } as CompositeFilterDescriptor;
            }

            if (currentFilter.operator === "isnotnull") {
                return {
                    logic: "and",
                    filters: [
                        { field: currentFilter.field, operator: "neq", value: null },
                        { field: currentFilter.field, operator: "neq", value: "" },
                        { field: currentFilter.field, operator: "neq", value: undefined },
                        { field: currentFilter.field, operator: "neq", value: 0 },
                    ],
                };
            }

            return f;
        });

        return {
            logic: filter.logic,
            filters: processedFilters,
        };
    }

    private normalizeFilterGroup(filter: CompositeFilterDescriptor): CompositeFilterDescriptor {
        if (!filter || !filter.filters || filter.filters.length === 0) {
            return filter;
        }

        const seenFields = new Set<string>();
        const resultFilters: (FilterDescriptor | CompositeFilterDescriptor)[] = [];

        for (let i = filter.filters.length - 1; i >= 0; i--) {
            const currentItem = filter.filters[i];

            if ((currentItem as CompositeFilterDescriptor).filters) {
                const normalizedGroup = this.normalizeFilterGroup(currentItem as CompositeFilterDescriptor);
                resultFilters.unshift(normalizedGroup);
                continue;
            }

            const currentFilter = currentItem as FilterDescriptor;
            const field = currentFilter.field;

            if (field && !seenFields.has(field.toString())) {
                resultFilters.unshift(currentFilter);
                seenFields.add(field.toString());
            } else if (currentFilter.operator === "isnull" || currentFilter.operator === "isnotnull") {
                resultFilters.forEach((f: any) => {
                    if (f.field === currentFilter.field) {
                        f.operator = currentFilter.operator;
                        f.value = currentFilter.value;
                    }
                });
            }
        }

        return {
            logic: filter.logic,
            filters: resultFilters,
        };
    }

    public onClearStartDate() {
        this.startDateInput.nativeElement.value = "";
        this.flightsFilterForm.controls["startDate"].setValue(null);
        this.onDateChosen(null);
    }

    public onClearEndDate() {
        this.endDateInput.nativeElement.value = "";
        this.flightsFilterForm.controls["endDate"].setValue(null);
        this.onDateChosen(null);
    }

    public getFileName() {
        let name = "FLIGHTS";
        this.helicopterList.forEach((h) => {
            if ((this.flightsFilterForm.controls["helicopters"].value as number[]).includes(h.id)) {
                name += "_" + h.registrationMark;
            }
        });
        name += ".xlsx";
        return name;
    }

    public filterGroup(event) {
        const filterValue = event.toLowerCase();

        const groupFilter: CompositeFilterDescriptor = {
            logic: "or",
            filters: [
                { field: "firstRefuelLocation", operator: "contains", value: filterValue },
                { field: "secondRefuelLocation", operator: "contains", value: filterValue },
            ],
        };

        let locationIndex = -1;

        this.state.filter?.filters.forEach((f: any, index) => {
            if (f.filters.find((fl) => fl.field === "firstRefuelLocation") && f.filters.find((fl) => fl.field === "secondRefuelLocation")) {
                locationIndex = index;
            }
        });

        if (locationIndex >= 0) {
            this.state.filter.filters[locationIndex] = groupFilter;
        } else {
            if (!this.state.filter) {
                this.state.filter = {
                    logic: "and",
                    filters: [],
                };
            }
            this.state.filter.filters.push(groupFilter);
        }

        this.loadItems();
    }

    public onDataChosen(event) {
        if (!event.isUserInput) return;
        switch (event.source.value) {
            case 0:
                this.currentDataFilter = DataFilter.All;
                this.data = this.originalData;
                break;
            case 1:
                this.currentDataFilter = DataFilter.Original;
                this.data = this.originalData.filter((d) => !d.isEdited);
                break;
            case 2:
                this.currentDataFilter = DataFilter.Edited;
                this.data = this.originalData.filter((d) => d.isEdited);
                break;
        }
        this.gridView = process(this.data, this.state);
    }
}
